import { Button, Drawer } from "@/components";
import { Alert } from "@/components/Alert";
import {
  ArrowLeft,
  CheckSuccess,
  ChevronDown,
  ChevronUp,
  Invoice,
} from "@/components/Icons";
import { Loading } from "@/components/Loading";
import { EcleticaTable } from "@/services/Ecletica/EcleticaTable";
import styles from "@/styles/containers/command.module.scss";
import { Currency } from "@/tools";
import Bugsnag from "@bugsnag/js";
import classNames from "classnames/bind";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import EcleticaCartItem from "./Product/EcleticaCartItem";
import Timer from "@/components/Timer";
const css = classNames.bind(styles);

export default function CommandEcletica({ onClose }) {
  const [table, setTable] = useState(null);
  const [tableItems, setTableItems] = useState([]);
  const [tableStatus, setTableStatus] = useState(null);

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [canCallWaiter, setCanCallWaiter] = useState(true);
  const [callingWaiter, setCallingWaiter] = useState(false);

  const [errorMessage, setErrorMessage] = useState(null);
  const [warningMessage, setWarningMessage] = useState(null);
  const [openErrorMessage, setOpenErrorMessage] = useState(null);
  const [openWarningMessage, setOpenWarningMessage] = useState(null);

  const [openClosedTable, setOpenClosedTable] = useState(false);
  const [closingTable, setClosingTable] = useState(false);

  useEffect(() => {
    loadTable();
  }, []);

  const loadTable = async () => {
    setLoading(true);

    try {
      const ecleticaTable = new EcleticaTable();

      const table = await ecleticaTable.getTable();
      const tableStatus = table?.status || "";

      let tableItems = [];

      if (tableStatus === "Fechando") {
        tableItems = table?.produtos || [];

        setTable(table);
      } else {
        tableItems = await ecleticaTable.getTableItems();
      }

      setTableItems(tableItems || []);
      setTableStatus(tableStatus);

      setLoading(false);
    } catch (err) {
      console.log(err);

      Bugsnag.notify(err);

      setErrorMessage(t("unableToLoadAccount"));
      setOpenErrorMessage(true);

      setLoading(false);
    }
  };

  const onPreClose = async () => {
    setClosingTable(true);
    try {
      const ecleticaTable = new EcleticaTable();
      await ecleticaTable.preClose();

      setOpenClosedTable(true);

      loadTable();
    } catch (err) {
      console.log(err);

      Bugsnag.notify(err);

      setErrorMessage(t("unableToCloseTable"));
      setOpenErrorMessage(true);
    }
    setClosingTable(false);
  };

  const onCallWaiter = async () => {
    setCallingWaiter(true);
    setAllowCallWaiter(false);

    try {
      const ecleticaTable = new EcleticaTable();
      await ecleticaTable.callWaiter();

      setWarningMessage({
        title: "O garçom foi chamado",
        subtitle:
          "Espere alguns instantes e um garçom irá atender a sua mesa em breve",
      });
      setOpenWarningMessage(true);

      setCanCallWaiter(false);
    } catch (err) {
      console.log(err);

      Bugsnag.notify(err);

      setErrorMessage(t("unableToCallWaiter"));
      setOpenErrorMessage(true);
    }

    setCallingWaiter(false);
  };

  const t = useTranslations();

  const hasServiceAmount =
    table?.total?.vlr_servico && +table?.total.vlr_servico > 0 ? true : false;

  const [allowCallWaiter, setAllowCallWaiter] = useState(true);

  return (
    <div className={styles.commandContainer}>
      <div className={styles.header}>
        <div className={styles.back} onClick={onClose}>
          <ArrowLeft />
        </div>
        <p className={styles.title}>{t("myAccount")}</p>
        {!loading && canCallWaiter && tableItems?.length > 0 && (
          <Button
            design="default"
            onClick={onCallWaiter}
            disabled={callingWaiter || !allowCallWaiter}
          >
            {t("callWaiter")}
            {!allowCallWaiter && (
              <div className="ml-5">
                <Timer
                  duration={30}
                  start={!allowCallWaiter}
                  onComplete={() => setAllowCallWaiter(true)}
                />
              </div>
            )}
          </Button>
        )}
      </div>

      {loading && (
        <div className={styles.loading}>
          <Loading />
        </div>
      )}

      {!loading && !tableItems?.length && (
        <div className={styles.empty}>
          <Invoice size={34} color="#8C8C8C" />
          <p className={styles.title}>{t("noOrdersYet")}</p>
          <p className={styles.subtitle}>{t("checkOrdersHere")}</p>
        </div>
      )}

      {!loading && tableItems?.length > 0 && (
        <>
          <EcleticaCartItem items={tableItems} />

          <div className={styles.footer}>
            {tableStatus && tableStatus !== "EmUso" && (
              <div className={styles.totals}>
                {expanded ? (
                  <>
                    <div
                      className="d-flex align-center justify-center w-100 mt-10 mb-20"
                      onClick={() => setExpanded(false)}
                    >
                      <ChevronDown />
                    </div>

                    <div className={styles.item}>
                      <span>{t("subtotal")}</span>
                      <p>
                        {Currency.formatCurrency(
                          table.total.vlr_total_produtos
                        )}
                      </p>
                    </div>

                    {hasServiceAmount ? (
                      <div className={styles.item}>
                        <span>
                          {t("serviceFee")}
                          {table.servico_obrigatorio !== "S"
                            ? ` (${t("optional")})`
                            : ""}
                        </span>
                        <p>
                          {Currency.formatCurrency(+table.total.vlr_servico)}
                        </p>
                      </div>
                    ) : null}
                  </>
                ) : null}

                <div className={styles.total}>
                  <span>{t("total")}</span>
                  {!expanded ? (
                    <div className={styles.chevron}>
                      <div
                        className="d-flex align-center"
                        onClick={() => setExpanded(true)}
                      >
                        <ChevronUp />
                      </div>
                    </div>
                  ) : null}
                  <p>{Currency.formatCurrency(table?.total_conta)}</p>
                </div>
              </div>
            )}

            <Button
              className="w-100"
              disabled={tableStatus === "Fechando"}
              onClick={onPreClose}
              loading={closingTable}
            >
              {tableStatus === "Fechando" ? t("tableClosed") : t("requestBill")}
            </Button>
          </div>
        </>
      )}

      <Alert
        title={t("tryAgain")}
        subtitle={errorMessage}
        open={!!openErrorMessage}
        onClose={() => setOpenErrorMessage(null)}
      />

      <Alert
        title={warningMessage?.title}
        subtitle={warningMessage?.subtitle}
        open={!!openWarningMessage}
        onClose={() => setOpenWarningMessage(null)}
      />

      <Drawer open={openClosedTable} alreadyDrawer direction="bottom-center">
        <div className="d-flex justify-center mb-20">
          <CheckSuccess />
        </div>
        <p className={styles.alertTitle}>{t("tableHasBeenClosed")}</p>
        <p className={styles.alertSubtitle}></p>
        <Button
          design="primary"
          className="w-100"
          onClick={() => setOpenClosedTable(false)}
        >
          {t("returnToTable")}
        </Button>
      </Drawer>
    </div>
  );
}
